import type { CSSProperties, FC } from 'react'

const SIZE = 50
const HEIGHT = 56
const CORNER = 11.33
const ARROW_W = 16
const ARROW_H = 8

interface Props {
  fillColor: string
  text?: string
  /** Extra inline styles — position, opacity, transform for crossfade. */
  style?: CSSProperties
}

/**
 * Bubble visual — rounded-rect body + downward arrow + centered letter.
 * Parent positions the bubble host via `style.transform` (translate3d).
 * Anchor point (semantic) is bottom-center of the containing element =
 * arrow tip.
 *
 * Geometry parity with iOS 238.2 BubbleView.swift + Android BubbleShape.kt:
 * 50×56 total, corner 11.33, arrow 16×8, white bold 18px "A", drop-shadow
 * 4px/6px black @ 0.2.
 */
export const Bubble: FC<Props> = ({ fillColor, text = 'A', style }) => {
  const bodyH = HEIGHT - ARROW_H
  const arrowCx = SIZE / 2
  // Trace the outline clockwise starting at top-left's corner-arc end.
  const pathD = [
    `M ${CORNER} 0`,
    `H ${SIZE - CORNER}`,
    `A ${CORNER} ${CORNER} 0 0 1 ${SIZE} ${CORNER}`,
    `V ${bodyH - CORNER}`,
    `A ${CORNER} ${CORNER} 0 0 1 ${SIZE - CORNER} ${bodyH}`,
    `H ${arrowCx + ARROW_W / 2}`,
    `L ${arrowCx} ${HEIGHT}`,
    `L ${arrowCx - ARROW_W / 2} ${bodyH}`,
    `H ${CORNER}`,
    `A ${CORNER} ${CORNER} 0 0 1 0 ${bodyH - CORNER}`,
    `V ${CORNER}`,
    `A ${CORNER} ${CORNER} 0 0 1 ${CORNER} 0`,
    'Z',
  ].join(' ')

  // Merge caller's style first, then enforce fixed size so it cannot be
  // overridden — the layout contract is 50×56.
  const containerStyle: CSSProperties = {
    position: 'absolute',
    pointerEvents: 'none',
    filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.2))',
    ...style,
    width: `${SIZE}px`,
    height: `${HEIGHT}px`,
  }

  return (
    <div style={containerStyle}>
      <svg width={SIZE} height={HEIGHT} viewBox={`0 0 ${SIZE} ${HEIGHT}`}>
        <path d={pathD} fill={fillColor} />
        <text
          x={SIZE / 2}
          y={bodyH / 2 + 1}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="white"
          fontFamily="system-ui, -apple-system, sans-serif"
          fontSize="18"
          fontWeight="700"
        >
          {text}
        </text>
      </svg>
    </div>
  )
}
